import type { Family, FamilyDef } from './types'

export const FAMILIES: Record<Family, FamilyDef> = {
  bitcoin: {
    id: 'bitcoin',
    label: 'Bitcoin',
    tagline: 'Tank blindé',
    color: '#f7931a',
    colorDark: '#7a4508',
    colorLight: '#ffc46b',
    emoji: '🟠',
    statBias: { hp: 1.13, attack: 0.92, defense: 1.05, speed: 0.72, energy: 0.9 },
    passive: { name: 'Hard Cap', desc: 'Réduit tous les dégâts subis de 12%.' },
    normals: [
      { name: 'Hash Punch', power: 46 },
      { name: 'Bloc Slam', power: 50 },
      { name: 'Nonce Jab', power: 43 },
      { name: 'Cold Storage Smash', power: 51 },
      { name: 'Difficulty Bump', power: 47 },
    ],
    specials: [
      { name: 'Halving Crush', power: 92, cost: 45 },
      { name: 'Proof of Pain', power: 84, cost: 40 },
      { name: '21 Million', power: 104, cost: 52 },
      { name: 'Genesis Slam', power: 66, cost: 30 },
      { name: 'Hashrate Overdrive', power: 88, cost: 42 },
    ],
    namePrefixes: ['Bit', 'Hash', 'Bloc', 'Halv', 'Sato', 'Mine'],
    quips: ['Je frappe depuis 2009.', 'Ton hype ne casse pas mes blocs.', 'HODL ça.', 'La patience est ma seule stratégie.'],
  },
  ethereum: {
    id: 'ethereum',
    label: 'Ethereum',
    tagline: 'Mage équilibré',
    color: '#8b5cf6',
    colorDark: '#3e2478',
    colorLight: '#c4b5fd',
    emoji: '🟣',
    statBias: { hp: 1.0, attack: 1.11, defense: 1.0, speed: 1.0, energy: 1.2 },
    passive: { name: 'Gas Refund', desc: 'Recharge +8 énergie à chaque tour.' },
    normals: [
      { name: 'Smart Slash', power: 48 },
      { name: 'Ether Bolt', power: 46 },
      { name: 'Gas Spike', power: 44 },
      { name: 'Merkle Strike', power: 49 },
      { name: 'Calldata Cut', power: 45 },
    ],
    specials: [
      { name: 'The Merge', power: 88, cost: 42 },
      { name: 'Solidity Storm', power: 80, cost: 38 },
      { name: 'Ultrasound Money', power: 102, cost: 50 },
      { name: 'Dencun Wave', power: 64, cost: 28 },
      { name: 'Slashing', power: 90, cost: 44 },
    ],
    namePrefixes: ['Ethe', 'Vita', 'Gwei', 'Merk', 'Sol', 'Beacon'],
    quips: ['Mon contrat est déjà signé.', 'Tu vas payer le gas.', 'Code is law.', 'Je t’ai simulé avant de venir.'],
  },
  base: {
    id: 'base',
    label: 'Base',
    tagline: 'Combo véloce',
    color: '#3b82f6',
    colorDark: '#173a80',
    colorLight: '#93c5fd',
    emoji: '🔵',
    statBias: { hp: 1.02, attack: 1.06, defense: 0.95, speed: 1.35, energy: 1.05 },
    passive: { name: 'Low Gas', desc: '26% de chance d’agir deux fois dans le tour.' },
    normals: [
      { name: 'Rollup Rush', power: 44 },
      { name: 'L2 Jab', power: 42 },
      { name: 'Blob Tap', power: 43 },
      { name: 'Sequencer Snap', power: 46 },
      { name: 'Bridge Hop', power: 45 },
    ],
    specials: [
      { name: 'Onchain Summer', power: 82, cost: 38 },
      { name: 'Sequencer Strike', power: 78, cost: 35 },
      { name: 'Flashblock', power: 60, cost: 26 },
      { name: 'Superchain Surge', power: 98, cost: 48 },
      { name: 'Based Barrage', power: 84, cost: 40 },
    ],
    namePrefixes: ['Base', 'Zip', 'Rol', 'Seq', 'Blu', 'Dash'],
    quips: ['Trop lent. Suivant.', 'Je finalise avant que tu cliques.', 'Onchain summer, baby.', 'Deux coups pour le prix d’un.'],
  },
  defi: {
    id: 'defi',
    label: 'DeFi',
    tagline: 'Vampire d’énergie',
    color: '#ec4899',
    colorDark: '#75184a',
    colorLight: '#f9a8d4',
    emoji: '💗',
    statBias: { hp: 0.99, attack: 1.08, defense: 0.97, speed: 1.08, energy: 1.0 },
    passive: { name: 'Yield Drain', desc: 'Vole 10 énergie à l’ennemi à chaque coup porté.' },
    normals: [
      { name: 'Flash Loan', power: 47 },
      { name: 'Rug Claw', power: 49 },
      { name: 'Sandwich Bite', power: 45 },
      { name: 'Oracle Twist', power: 44 },
      { name: 'Slippage Slash', power: 46 },
    ],
    specials: [
      { name: 'Liquidation', power: 90, cost: 44 },
      { name: 'Impermanent Doom', power: 84, cost: 40 },
      { name: 'Vampire Attack', power: 66, cost: 30 },
      { name: 'Max Leverage', power: 106, cost: 54 },
      { name: 'Yield Storm', power: 86, cost: 42 },
    ],
    namePrefixes: ['Yield', 'Lique', 'Farm', 'Swap', 'Vamp', 'Apy'],
    quips: ['Ton énergie ? Mon rendement.', 'Position sous-collatéralisée détectée.', 'Je te liquide à l’amiable.', 'Chaque coup me rapporte.'],
  },
  meme: {
    id: 'meme',
    label: 'Meme',
    tagline: 'Chaos critique',
    color: '#ef4444',
    colorDark: '#7c1d1d',
    colorLight: '#fca5a5',
    emoji: '🔴',
    statBias: { hp: 0.98, attack: 1.16, defense: 0.9, speed: 1.14, energy: 1.0 },
    passive: { name: 'To The Moon', desc: '+18% de chance de critique, critiques x2.2.' },
    normals: [
      { name: 'Shitpost Slap', power: 45 },
      { name: 'FOMO Bite', power: 47 },
      { name: 'Copium Cloud', power: 43 },
      { name: 'Ratio Hit', power: 46 },
      { name: 'Cringe Throw', power: 44 },
    ],
    specials: [
      { name: 'Pump & Dump', power: 86, cost: 40 },
      { name: 'Diamond Paws', power: 80, cost: 36 },
      { name: 'To The Moon', power: 108, cost: 56 },
      { name: 'Wen Lambo', power: 62, cost: 28 },
      { name: 'Rugpull Reversal', power: 88, cost: 43 },
    ],
    namePrefixes: ['Doge', 'Pepe', 'Wojak', 'Moon', 'Wen', 'Giga'],
    quips: ['gm. ngmi.', 'Ratio + t’es pas basé.', 'Je mise tout. Toujours.', 'Mes crits sont un mème.'],
  },
  stable: {
    id: 'stable',
    label: 'Stablecoin',
    tagline: 'Bouclier soigneur',
    color: '#34d399',
    colorDark: '#0f5c40',
    colorLight: '#86efac',
    emoji: '🟢',
    statBias: { hp: 1.15, attack: 0.89, defense: 1.13, speed: 0.9, energy: 1.1 },
    passive: { name: 'Peg Stable', desc: 'Régénère 3% des HP max à chaque tour.' },
    normals: [
      { name: 'Peg Punch', power: 44 },
      { name: 'Collateral Kick', power: 46 },
      { name: 'Audit Slap', power: 43 },
      { name: 'Reserve Ram', power: 47 },
      { name: 'T-Bill Thump', power: 45 },
    ],
    specials: [
      { name: 'Full Reserve', power: 78, cost: 36 },
      { name: 'Depeg Panic', power: 84, cost: 42 },
      { name: 'Mint & Burn', power: 98, cost: 50 },
      { name: 'Redemption', power: 56, cost: 30 },
      { name: 'Algorithmic Anomaly', power: 86, cost: 40 },
    ],
    namePrefixes: ['Sta', 'Peg', 'Teth', 'Circ', 'Dai', 'Vault'],
    quips: ['Zéro volatilité. Zéro pitié.', 'Je tiens le peg, je te tiens toi.', 'Audité, assuré, imbattable.', 'Ta panique ne me dépegge pas.'],
  },
}

export const FAMILY_LIST = Object.values(FAMILIES)

// Le cycle d'avantages — chaque famille en bat une et perd contre une:
// Bitcoin > Ethereum > DeFi > Stablecoin > Meme > Base > Bitcoin
// (l'or originel domine son héritier; le smart contract fixe les règles de la
// DeFi; le yield fait sauter le peg; la stabilité éteint le hype; les memes
// envahissent la L2; la vitesse L2 déborde le dinosaure lent.)
// Direction choisie par simulation: chaque arête part d'un rapport naturel
// de 40-51% pour atterrir vers 60-70% avec le bonus — un avantage, pas un
// hard counter (voir README, section équilibrage).
export const BEATS: Record<Family, Family> = {
  bitcoin: 'ethereum',
  ethereum: 'defi',
  defi: 'stable',
  stable: 'meme',
  meme: 'base',
  base: 'bitcoin',
}

export const BEATEN_BY = Object.fromEntries(
  Object.entries(BEATS).map(([winner, loser]) => [loser, winner]),
) as Record<Family, Family>

export const MATCHUP_STRONG = 1.05
export const MATCHUP_WEAK = 0.95

// Matrice de types — multiplicateur de dégâts ATTAQUANT → DÉFENSEUR.
// CALIBRÉE PAR SIMULATION (audit phase 3.2, scripts/matchup-sim.ts): en classé
// normalisé, chaque matchup vise le CYCLE (bitcoin>eth>defi>stable>meme>base>
// bitcoin) à ~58% pour l'attaquant favorisé / 42% pour le défavorisé, et ~50%
// hors cycle. La matrice COMPENSE les asymétries de statBias/passifs (qui, non
// corrigées, produisaient des matchups à 21-74%). Fourchette obtenue: 37-59%.
// Ne PAS éditer à la main: re-tuner via le script et re-figer.
export const MATCHUP: Record<Family, Record<Family, number>> = {
  //          bitcoin  ethereum  base   defi   meme   stable
  bitcoin: { bitcoin: 1.0, ethereum: 1.03, base: 0.93, defi: 1.0, meme: 1.02, stable: 1.0 },
  ethereum: { bitcoin: 0.97, ethereum: 1.0, base: 1.01, defi: 1.05, meme: 1.0, stable: 1.0 },
  base: { bitcoin: 1.07, ethereum: 0.99, base: 1.0, defi: 1.01, meme: 0.96, stable: 1.01 },
  defi: { bitcoin: 1.0, ethereum: 0.95, base: 0.99, defi: 1.0, meme: 1.01, stable: 0.98 },
  meme: { bitcoin: 1.01, ethereum: 0.97, base: 1.04, defi: 0.99, meme: 1.0, stable: 0.94 },
  stable: { bitcoin: 0.97, ethereum: 1.03, base: 0.99, defi: 1.02, meme: 1.06, stable: 1.0 },
}

/** Multiplicateur de dégâts de l'attaquant selon la matrice de types. */
export function matchupMultiplier(attacker: Family, defender: Family): number {
  return MATCHUP[attacker][defender]
}

/**
 * Efficacité contre un défenseur pouvant avoir DEUX types — multiplicateurs
 * composés (ex. ×1,05 × ×1,1 = faiblesse cumulée). L'attaquant frappe avec son
 * type PRIMAIRE.
 */
export function typeEffectiveness(attacker: Family, defPrimary: Family, defSecondary?: Family): number {
  let m = MATCHUP[attacker][defPrimary]
  if (defSecondary && defSecondary !== defPrimary) m *= MATCHUP[attacker][defSecondary]
  return m
}

/** Faiblesses/résistances cumulées d'un monstre (mono ou double type) — pour l'affichage. */
export function combinedTypeChart(
  primary: Family,
  secondary?: Family,
): { weakTo: Family[]; resists: Family[] } {
  const weakTo: Family[] = []
  const resists: Family[] = []
  for (const atk of FAM_IDS) {
    const e = typeEffectiveness(atk, primary, secondary)
    if (e > 1.001) weakTo.push(atk)
    else if (e < 0.999) resists.push(atk)
  }
  return { weakTo, resists }
}

const FAM_IDS = Object.keys(MATCHUP) as Family[]

/**
 * Vue « fiche » de la classe: fort/faible EN ATTAQUE (mult sur l'adversaire) et
 * résiste/vulnérable EN DÉFENSE (mult que l'adversaire inflige). Pour l'affichage.
 */
export function typeChartFor(family: Family): {
  strongVs: Family[]
  weakVs: Family[]
  resists: Family[]
  vulnerable: Family[]
} {
  const strongVs: Family[] = []
  const weakVs: Family[] = []
  const resists: Family[] = []
  const vulnerable: Family[] = []
  for (const other of FAM_IDS) {
    if (other === family) continue
    if (MATCHUP[family][other] > 1) strongVs.push(other)
    else if (MATCHUP[family][other] < 1) weakVs.push(other)
    if (MATCHUP[other][family] < 1) resists.push(other)
    else if (MATCHUP[other][family] > 1) vulnerable.push(other)
  }
  return { strongVs, weakVs, resists, vulnerable }
}

export const RARITY_INFO: Record<
  string,
  { label: string; mult: number; weight: number; color: string }
> = {
  common: { label: 'Common', mult: 1.0, weight: 60, color: '#9ca3af' },
  rare: { label: 'Rare', mult: 1.12, weight: 25, color: '#22d3ee' },
  epic: { label: 'Epic', mult: 1.26, weight: 12, color: '#8b5cf6' },
  legendary: { label: 'Legendary', mult: 1.45, weight: 3, color: '#f7931a' },
}
